import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

const UK_SPORTS = [
  'Football', 'Rugby', 'Cricket', 'Tennis', 'Swimming', 'Athletics', 
  'Basketball', 'Hockey', 'Netball', 'Gymnastics', 'Golf', 'Martial Arts', 'Other'
];

export default function AddChild() {
  const navigate = useNavigate();
  const [coaches, setCoaches] = useState([]);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    gender: '',
    maternal_height: '',
    paternal_height: '',
    assigned_coach_id: '',
    assigned_coach_name: '',
    primary_sport: '',
    mobile_phone: '',
    email: '',
    address: '',
    emergency_contact: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadCoaches();
  }, []);

  const loadCoaches = async () => {
    try {
      const response = await axios.get(`${API}/auth/coaches`);
      setCoaches(response.data.coaches || []);
    } catch (error) {
      // Coaches may not be available - continue without them
    }
  };

  const handleCoachChange = (coachId) => {
    const coach = coaches.find(c => c.id === coachId);
    setFormData(prev => ({
      ...prev,
      assigned_coach_id: coachId,
      assigned_coach_name: coach?.full_name || ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        date_of_birth: new Date(formData.date_of_birth).toISOString(),
        maternal_height: formData.maternal_height ? parseFloat(formData.maternal_height) : null,
        paternal_height: formData.paternal_height ? parseFloat(formData.paternal_height) : null
      };

      await axios.post(`${API}/children`, payload);
      toast.success('Child profile created successfully!');
      navigate('/');
    } catch (error) {
      toast.error('Failed to create child profile');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <div className="bg-zinc-950 shadow-sm border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <Button
            data-testid="back-button"
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-white mb-4 rounded-lg"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
          <h1 className="text-4xl font-bold text-white" data-testid="add-child-title">
            Add New Child
          </h1>
          <p className="text-gray-400 mt-2">Create a profile to start tracking growth and performance</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="bg-zinc-900 rounded-2xl shadow-lg p-8 border border-zinc-800">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="add-child-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                  First Name *
                </Label>
                <Input
                  data-testid="first-name-input"
                  required
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="h-12 bg-zinc-800 border-zinc-700 focus:border-[#02b5ff] focus:ring-[#02b5ff] text-white rounded-xl"
                />
              </div>

              <div>
                <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                  Last Name *
                </Label>
                <Input
                  data-testid="last-name-input"
                  required
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="h-12 bg-zinc-800 border-zinc-700 focus:border-[#02b5ff] focus:ring-[#02b5ff] text-white rounded-xl"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                  Date of Birth *
                </Label>
                <Input
                  data-testid="dob-input"
                  type="date"
                  required
                  value={formData.date_of_birth}
                  onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                  className="h-12 bg-zinc-800 border-zinc-700 focus:border-[#02b5ff] focus:ring-[#02b5ff] text-white rounded-xl"
                />
              </div>

              <div>
                <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                  Gender *
                </Label>
                <Select
                  required
                  value={formData.gender}
                  onValueChange={(value) => setFormData({ ...formData, gender: value })}
                >
                  <SelectTrigger data-testid="gender-select" className="h-12 bg-zinc-800 border-zinc-700 text-white rounded-xl">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700 rounded-xl">
                    <SelectItem value="male" className="rounded-lg text-white">Male</SelectItem>
                    <SelectItem value="female" className="rounded-lg text-white">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border-t border-zinc-700 pt-6 mt-6">
              <h3 className="text-lg font-bold text-white mb-4">
                Parental Heights (for predictions)
              </h3>
              <p className="text-sm text-gray-400 mb-4">Optional but helps calculate predicted adult height</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                    Maternal Height (cm)
                  </Label>
                  <Input
                    data-testid="maternal-height-input"
                    type="number"
                    step="0.1"
                    value={formData.maternal_height}
                    onChange={(e) => setFormData({ ...formData, maternal_height: e.target.value })}
                    className="h-12 bg-zinc-800 border-zinc-700 focus:border-[#02b5ff] focus:ring-[#02b5ff] text-white rounded-xl"
                    placeholder="e.g., 165.0"
                  />
                </div>

                <div>
                  <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                    Paternal Height (cm)
                  </Label>
                  <Input
                    data-testid="paternal-height-input"
                    type="number"
                    step="0.1"
                    value={formData.paternal_height}
                    onChange={(e) => setFormData({ ...formData, paternal_height: e.target.value })}
                    className="h-12 bg-zinc-800 border-zinc-700 focus:border-[#02b5ff] focus:ring-[#02b5ff] text-white rounded-xl"
                    placeholder="e.g., 180.0"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-zinc-700 pt-6 mt-6">
              <h3 className="text-lg font-bold text-white mb-4">
                Sport & Coach Assignment
              </h3>
              <p className="text-sm text-gray-400 mb-4">Optional - assign to a coach and select primary sport</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                    Assigned Coach
                  </Label>
                  <Select
                    value={formData.assigned_coach_id}
                    onValueChange={handleCoachChange}
                  >
                    <SelectTrigger data-testid="coach-select" className="h-12 bg-zinc-800 border-zinc-700 text-white rounded-xl">
                      <SelectValue placeholder="Select coach" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700 rounded-xl max-h-60">
                      {coaches.map(coach => (
                        <SelectItem key={coach.id} value={coach.id} className="rounded-lg text-white">
                          {coach.full_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                    Primary Sport
                  </Label>
                  <Select
                    value={formData.primary_sport}
                    onValueChange={(value) => setFormData({ ...formData, primary_sport: value })}
                  >
                    <SelectTrigger data-testid="sport-select" className="h-12 bg-zinc-800 border-zinc-700 text-white rounded-xl">
                      <SelectValue placeholder="Select sport" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700 rounded-xl max-h-60">
                      {UK_SPORTS.map(sport => (
                        <SelectItem key={sport} value={sport} className="rounded-lg text-white">{sport}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="border-t border-zinc-700 pt-6 mt-6">
              <h3 className="text-lg font-bold text-white mb-4">
                Contact Details
              </h3>
              <p className="text-sm text-gray-400 mb-4">Parent/Guardian contact information</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                    Mobile Phone
                  </Label>
                  <Input
                    data-testid="mobile-phone-input"
                    type="tel"
                    value={formData.mobile_phone}
                    onChange={(e) => setFormData({ ...formData, mobile_phone: e.target.value })}
                    className="h-12 bg-zinc-800 border-zinc-700 focus:border-[#02b5ff] focus:ring-[#02b5ff] text-white rounded-xl"
                    placeholder="e.g., 07123 456789"
                  />
                </div>

                <div>
                  <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                    Email Address
                  </Label>
                  <Input
                    data-testid="email-input"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="h-12 bg-zinc-800 border-zinc-700 focus:border-[#02b5ff] focus:ring-[#02b5ff] text-white rounded-xl"
                    placeholder="e.g., parent@email.com"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                    Address
                  </Label>
                  <Input
                    data-testid="address-input"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="h-12 bg-zinc-800 border-zinc-700 focus:border-[#02b5ff] focus:ring-[#02b5ff] text-white rounded-xl"
                    placeholder="e.g., 123 Main Street, London"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                    Emergency Contact
                  </Label>
                  <Input
                    data-testid="emergency-contact-input"
                    value={formData.emergency_contact}
                    onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
                    className="h-12 bg-zinc-800 border-zinc-700 focus:border-[#02b5ff] focus:ring-[#02b5ff] text-white rounded-xl"
                    placeholder="e.g., John Doe - 07987 654321"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-4 pt-6">
              <Button
                data-testid="cancel-button"
                type="button"
                onClick={() => navigate('/')}
                className="h-12 px-8 bg-zinc-800 hover:bg-zinc-700 text-gray-300 font-semibold rounded-full transition-all"
              >
                Cancel
              </Button>
              <Button
                data-testid="create-child-button"
                type="submit"
                disabled={submitting}
                className="h-12 px-8 bg-gradient-to-r from-[#02b5ff] to-[#0099dd] hover:from-[#0099dd] hover:to-[#0088cc] text-white font-semibold rounded-full flex-1 shadow-lg hover:shadow-xl transition-all"
              >
                {submitting ? 'Creating...' : 'Create Profile'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}